/**
 * EmailBillingBlock — renders the model's billing suggestion
 * (Phase X.3.4.5 → X.3.4.6).
 *
 * Operator-locked microcopy:
 *   ⏱️ Suggested billing
 *   0.2 hours · Regular Update      (X.3.4.6: no double quotes; soft chip)
 *   <warm educational rationale>    (X.3.4.6 prompt rationale voice)
 *
 * NO "your call" / "your discretion" / "the AI suggests" framing —
 * the block IS the suggestion; operator autonomy is implicit.
 *
 * X.3.4.6 visual: sage-cream Peace palette (different from the warm-
 * cream/gold annotation block above it).
 *   - bg            #ECF1EE  (sage-cream ground)
 *   - left accent   #52796F  (warm sage)
 *   - heading       #1B4332  (forest green)
 *   - body          #2D3A35  (dark text)
 */

import { type ReactNode } from 'react';
import type { EmailBillingSuggestion } from '@/lib/api';

export interface EmailBillingBlockProps {
  suggestion: EmailBillingSuggestion;
}

/**
 * Format the hours value with one decimal so 0.2 doesn't render as "0.2"
 * vs the integer model output "0.2" (defensive in case the model emits
 * an int by mistake).
 */
function formatHours(h: number): string {
  return h.toFixed(1);
}

export function EmailBillingBlock({ suggestion }: EmailBillingBlockProps): ReactNode {
  const containerStyle = {
    backgroundColor: '#ECF1EE',
    borderLeftColor: 'var(--color-sage-400)',
  } as const;
  const headingStyle = { color: 'var(--color-sage-800)' } as const;
  const bodyStyle = { color: 'var(--color-ink)' } as const;
  const categoryChipStyle = {
    backgroundColor: 'var(--color-paper)',
    borderColor: '#C9D6CF',
    color: 'var(--color-sage-800)',
  } as const;

  return (
    <section
      className="rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-1"
      style={containerStyle}
      data-testid="email-billing-block"
      aria-label="Suggested billing"
    >
      <h3 className="text-sm font-semibold flex items-center gap-1.5" style={headingStyle}>
        <span aria-hidden="true">⏱️</span>
        <span>Suggested billing</span>
      </h3>
      <p className="text-base font-semibold flex items-center gap-2 flex-wrap" style={bodyStyle}>
        <span>{formatHours(suggestion.hours)} hours</span>
        <span className="text-ink-soft" aria-hidden="true">·</span>
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full border"
          style={categoryChipStyle}
          data-testid="email-billing-category"
        >
          {suggestion.category}
        </span>
      </p>
      <p className="text-sm leading-relaxed" style={bodyStyle}>
        {suggestion.rationale}
      </p>
    </section>
  );
}
